import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { graphQlClient } from '@config/graphqlClient';
import TasksGraphQL from '@graphql/task.queries';
import { Task } from '@models/task.model';
import { SnackType } from '@models/common.model';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useAppDispatch } from '@redux/hooks';
import { useTranslation } from '@hooks/useTranslation';

/** Query key prefix of the single-task query; the org-events subscription invalidates by it. */
export const TASK_QUERY_KEY = 'task';

interface UseTaskReturn {
  task: Task | null;
  loading: boolean;
  error: string | null;
  fetchTask: (taskId: string) => void;
  refetch: () => void;
  setTask: (task: Task | null) => void;
}

// API function
const fetchTaskAPI = async (taskId: string): Promise<Task> => {
  const data: { getTaskById: Task } = await graphQlClient.request(
    TasksGraphQL.queries.getTaskById,
    { taskId },
  );

  if (!data.getTaskById) {
    throw new Error('Task not found');
  }

  return data.getTaskById;
};

export const useTask = (initialTaskId?: string): UseTaskReturn => {
  const [currentTaskId, setCurrentTaskId] = React.useState<string | undefined>(initialTaskId);
  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');

  const {
    data: task = null,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: [TASK_QUERY_KEY, currentTaskId],
    queryFn: () => fetchTaskAPI(currentTaskId!),
    enabled: !!currentTaskId,
  });

  // Handle errors separately
  React.useEffect(() => {
    if (queryError) {
      const errorMessage =
        (queryError as any)?.response?.errors[0]?.message || 
        queryError?.message || 
        localeCommon['requestError'];
      
      dispatch(
        appendActionMessage({
          message: errorMessage,
          type: SnackType.ERROR,
        }),
      );
    }
  }, [queryError, dispatch, localeCommon]);

  const fetchTask = (taskId: string) => {
    setCurrentTaskId(taskId);
  };

  const setTask = (taskData: Task | null) => {
    if (taskData === null) {
      // Clear the current task by resetting the taskId
      setCurrentTaskId(undefined);
    } else {
      // For backwards compatibility - this doesn't really make sense with React Query
      // but we'll provide it for now
      console.warn('setTask with data is deprecated when using React Query. Use fetchTask() instead.');
    }
  };

  return {
    task,
    loading,
    error: queryError?.message || null,
    fetchTask,
    refetch,
    setTask,
  };
};
